/**
 * SiteDetailPage -- The primary CMS workspace for a single site.
 *
 * Shows template selector, section manager with drag-and-drop, and
 * content tabs for career entries, skills, education, certificates, patents.
 * Each tab allows visibility toggling, reordering, and variant/override selection.
 */
import { useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSiteStore, useSiteActions } from '../../state/cms/useSiteStore';
import { useContentStore, useContentActions } from '../../state/cms/useContentStore';
import { cmsSitesApi } from '../../services/cms.api';
import { Panel } from '../../components/ui/Panel';
import { Button } from '../../components/ui/Button';
import { LoadingState } from '../../components/ui/LoadingState';
import { SortableList } from '../../components/cms/SortableList';
import { VisibilityToggle } from '../../components/cms/VisibilityToggle';
import { PublishButton } from '../../components/cms/PublishButton';
import { AiAssistButton } from '../../components/cms/AiAssistButton';
import { AiSuggestionPanel } from '../../components/cms/AiSuggestionPanel';
import { TailorPanel } from '../../components/cms/TailorPanel';
import { VariantPickerDrawer } from '../../components/cms/VariantPickerDrawer';
import { useAiAssist } from '../../hooks/useAiAssist';
import { aiApi } from '../../services/ai.api';
import { logger } from '../../services/logger.client';
import type {
  CmsSite,
  SiteSection,
  SiteCareerEntry,
  SiteSkill,
  SiteEducation,
  SiteCertificate,
  SitePatent,
  CareerEntryVariant,
} from '../../types/cms.types';
import type {
  PersonalizeTemplateData,
  TemplateRecommendation,
} from '../../types/ai.types';

const log = logger.child({ module: 'SiteDetailPage' });

type ContentTab = 'career' | 'skills' | 'education' | 'certificates' | 'patents';

const SECTION_LABELS: Record<string, string> = {
  personal_info: 'Personal Info',
  career: 'Career',
  skills: 'Skills',
  education: 'Education',
  certificates: 'Certificates',
  patents: 'Patents',
};

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export function SiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const sites = useSiteStore((s) => s.sites);
  const sections = useSiteStore((s) => s.sections);
  const templates = useSiteStore((s) => s.templates);
  const isLoading = useSiteStore((s) => s.isLoading);
  const isPublishing = useSiteStore((s) => s.isPublishing);
  const error = useSiteStore((s) => s.error);
  const {
    fetchSites,
    fetchSections,
    fetchTemplates,
    updateSections,
    updateSite,
    publishSite,
  } = useSiteActions();

  const personalInfo = useContentStore((s) => s.personalInfo);
  const careerEntries = useContentStore((s) => s.careerEntries);
  const skills = useContentStore((s) => s.skills);
  const education = useContentStore((s) => s.education);
  const certificates = useContentStore((s) => s.certificates);
  const patents = useContentStore((s) => s.patents);
  const variants = useContentStore((s) => s.variants);
  const {
    fetchPersonalInfo,
    fetchCareerEntries,
    fetchSkills,
    fetchEducation,
    fetchCertificates,
    fetchPatents,
    fetchVariants,
  } = useContentActions();

  const [activeTab, setActiveTab] = useState<ContentTab>('career');
  const [tailorOpen, setTailorOpen] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /* ---- Variant picker drawer state ---- */
  const [variantPickerOpen, setVariantPickerOpen] = useState(false);
  const [variantPickerEntry, setVariantPickerEntry] = useState<{
    entryId: string;
    entryLabel: string;
    currentVariantId: string | null;
  } | null>(null);

  function openVariantPicker(entry: SiteCareerEntry) {
    setVariantPickerEntry({
      entryId: entry.career_entry_id,
      entryLabel: getCareerEntryName(entry.career_entry_id),
      currentVariantId: entry.variant_id,
    });
    setVariantPickerOpen(true);
  }

  /** Wrap an async API call with error feedback (auto-clears after 5s). */
  const withErrorFeedback = useCallback(async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Operation failed';
      log.error('Site update failed', { error: msg });
      setUpdateError(msg);
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      errorTimerRef.current = setTimeout(() => setUpdateError(null), 5000);
    }
  }, []);

  /* ---- AI Assist: Template personalization ---- */
  const personalizeAi = useAiAssist<
    { profile_summary: string; target_role: string; template_id: string },
    PersonalizeTemplateData
  >({
    apiCall: (input) => aiApi.personalizeTemplate(input),
  });

  /** Custom renderer for template recommendations. */
  function renderRecommendations(data: unknown): ReactNode {
    const typed = data as PersonalizeTemplateData;
    if (!typed.recommendations || typed.recommendations.length === 0) {
      return <p className="ai-suggestion-panel__text">No recommendations available.</p>;
    }
    return (
      <div className="ai-suggestion-panel__recommendations">
        {typed.recommendations.map((rec: TemplateRecommendation, i: number) => (
          <div key={i} className="ai-suggestion-panel__rec-item">
            <div className="ai-suggestion-panel__rec-type">{rec.type.replace(/_/g, ' ')}</div>
            <div className="ai-suggestion-panel__rec-suggestion">{rec.suggestion}</div>
            <div className="ai-suggestion-panel__rec-rationale">{rec.rationale}</div>
          </div>
        ))}
      </div>
    );
  }

  /* Site content config state (loaded from site-specific endpoints) */
  const [siteCareerEntries, setSiteCareerEntries] = useState<SiteCareerEntry[]>([]);
  const [siteSkills, setSiteSkills] = useState<SiteSkill[]>([]);
  const [siteEducation, setSiteEducation] = useState<SiteEducation[]>([]);
  const [siteCertificates, setSiteCertificates] = useState<SiteCertificate[]>([]);
  const [sitePatents, setSitePatents] = useState<SitePatent[]>([]);

  const site: CmsSite | undefined = sites.find((s) => s.id === id);

  const hasFetched = useSiteStore((s) => s.hasFetched);

  /* ---- Load data on mount ---- */
  useEffect(() => {
    if (!hasFetched) void fetchSites();
    void fetchTemplates();
  }, [hasFetched, fetchSites, fetchTemplates]);

  useEffect(() => {
    if (!id) return;
    log.info('Loading site detail', { id });
    void fetchSections(id);
    void fetchPersonalInfo();
    void fetchCareerEntries();
    void fetchSkills();
    void fetchEducation();
    void fetchCertificates();
    void fetchPatents();

    /* Load site-specific content config */
    void loadSiteContent(id);
  }, [id, fetchSections, fetchPersonalInfo, fetchCareerEntries, fetchSkills, fetchEducation, fetchCertificates, fetchPatents]);

  const loadSiteContent = useCallback(async (siteId: string) => {
    try {
      const [career, sk, edu, cert, pat] = await Promise.all([
        cmsSitesApi.getCareerEntries(siteId),
        cmsSitesApi.getSkills(siteId),
        cmsSitesApi.getEducation(siteId),
        cmsSitesApi.getCertificates(siteId),
        cmsSitesApi.getPatents(siteId),
      ]);
      setSiteCareerEntries(career);
      setSiteSkills(sk);
      setSiteEducation(edu);
      setSiteCertificates(cert);
      setSitePatents(pat);
    } catch (err) {
      log.error('Failed to load site content config', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, []);

  /* ---- Load variants for all career entries ---- */
  /* Variant fetching is deferred until templates have loaded to prevent
     HTTP/1.1 connection pool starvation (6-slot limit). Firing 40+ variant
     requests simultaneously with templates causes templates to be queued
     indefinitely behind variants, never rendering the template panel.
     `variants` is intentionally excluded from deps: including it causes an
     infinite re-fetch loop because each variant completion re-triggers this
     effect while other entries are still in-flight. */
  useEffect(() => {
    if (templates.length === 0) return;
    for (const entry of careerEntries) {
      if (!variants[entry.id]) {
        void fetchVariants(entry.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careerEntries, fetchVariants, templates.length]);

  /* ---- Section reorder ---- */
  function handleSectionReorder(reordered: SiteSection[]) {
    if (!id) return;
    const updated = reordered.map((s, i) => ({
      ...s,
      display_order: i,
    }));
    void updateSections(id, updated);
  }

  function handleSectionVisibility(sectionId: string, isVisible: boolean) {
    if (!id) return;
    const updated = sections.map((s) =>
      s.id === sectionId ? { ...s, is_visible: isVisible } : s,
    );
    void updateSections(id, updated);
  }

  /* ---- Template selection ---- */
  function handleSelectTemplate(templateId: string) {
    if (!id || !site) return;
    void updateSite(id, { template_id: templateId });
  }

  /* ---- Career entry handlers ---- */
  async function handleCareerReorder(reordered: SiteCareerEntry[]) {
    if (!id) return;
    const updated = reordered.map((e, i) => ({ ...e, display_order: i }));
    setSiteCareerEntries(updated);
    await withErrorFeedback(() => cmsSitesApi.updateCareerEntries(id, updated));
  }

  async function handleCareerVisibility(entryId: string, isVisible: boolean) {
    if (!id) return;
    const updated = siteCareerEntries.map((e) =>
      e.id === entryId ? { ...e, is_visible: isVisible } : e,
    );
    setSiteCareerEntries(updated);
    await withErrorFeedback(() => cmsSitesApi.updateCareerEntries(id, updated));
  }

  async function handleVariantSelect(entryId: string, variantId: string | null) {
    if (!id) return;
    const updated = siteCareerEntries.map((e) =>
      e.career_entry_id === entryId ? { ...e, variant_id: variantId } : e,
    );
    setSiteCareerEntries(updated);
    await withErrorFeedback(() => cmsSitesApi.updateCareerEntries(id, updated));
  }

  /* ---- Skills handlers ---- */
  async function handleSkillReorder(reordered: SiteSkill[]) {
    if (!id) return;
    const updated = reordered.map((s, i) => ({ ...s, display_order: i }));
    setSiteSkills(updated);
    await withErrorFeedback(() => cmsSitesApi.updateSkills(id, updated));
  }

  async function handleSkillVisibility(skillId: string, isVisible: boolean) {
    if (!id) return;
    const updated = siteSkills.map((s) =>
      s.id === skillId ? { ...s, is_visible: isVisible } : s,
    );
    setSiteSkills(updated);
    await withErrorFeedback(() => cmsSitesApi.updateSkills(id, updated));
  }

  /* ---- Education handlers ---- */
  async function handleEducationReorder(reordered: SiteEducation[]) {
    if (!id) return;
    const updated = reordered.map((e, i) => ({ ...e, display_order: i }));
    setSiteEducation(updated);
    await withErrorFeedback(() => cmsSitesApi.updateEducation(id, updated));
  }

  async function handleEducationVisibility(eduId: string, isVisible: boolean) {
    if (!id) return;
    const updated = siteEducation.map((e) =>
      e.id === eduId ? { ...e, is_visible: isVisible } : e,
    );
    setSiteEducation(updated);
    await withErrorFeedback(() => cmsSitesApi.updateEducation(id, updated));
  }

  /* ---- Certificate handlers ---- */
  async function handleCertificateReorder(reordered: SiteCertificate[]) {
    if (!id) return;
    const updated = reordered.map((c, i) => ({ ...c, display_order: i }));
    setSiteCertificates(updated);
    await withErrorFeedback(() => cmsSitesApi.updateCertificates(id, updated));
  }

  async function handleCertificateVisibility(certId: string, isVisible: boolean) {
    if (!id) return;
    const updated = siteCertificates.map((c) =>
      c.id === certId ? { ...c, is_visible: isVisible } : c,
    );
    setSiteCertificates(updated);
    await withErrorFeedback(() => cmsSitesApi.updateCertificates(id, updated));
  }

  /* ---- Patent handlers ---- */
  async function handlePatentReorder(reordered: SitePatent[]) {
    if (!id) return;
    const updated = reordered.map((p, i) => ({ ...p, display_order: i }));
    setSitePatents(updated);
    await withErrorFeedback(() => cmsSitesApi.updatePatents(id, updated));
  }

  async function handlePatentVisibility(patentId: string, isVisible: boolean) {
    if (!id) return;
    const updated = sitePatents.map((p) =>
      p.id === patentId ? { ...p, is_visible: isVisible } : p,
    );
    setSitePatents(updated);
    await withErrorFeedback(() => cmsSitesApi.updatePatents(id, updated));
  }

  /* ---- Lookup helpers ---- */
  function getCareerEntryName(careerEntryId: string): string {
    const entry = careerEntries.find((e) => e.id === careerEntryId);
    return entry ? `${entry.company} - ${entry.title}` : 'Unknown Entry';
  }

  function getSkillName(skillId: string): string {
    const skill = skills.find((s) => s.id === skillId);
    return skill ? skill.name : 'Unknown Skill';
  }

  function getEducationName(educationId: string): string {
    const edu = education.find((e) => e.id === educationId);
    return edu ? `${edu.institution} - ${edu.degree}` : 'Unknown Education';
  }

  function getCertificateName(certificateId: string): string {
    const cert = certificates.find((c) => c.id === certificateId);
    return cert ? cert.name : 'Unknown Certificate';
  }

  function getPatentTitle(patentId: string): string {
    const pat = patents.find((p) => p.id === patentId);
    return pat ? pat.title : 'Unknown Patent';
  }

  function getVariantsForEntry(careerEntryId: string): CareerEntryVariant[] {
    return variants[careerEntryId] ?? [];
  }

  /* ---- Render ---- */
  return (
    <div className="cms-page">
      <header className="cms-page__header">
        <Link to="/cms/sites" className="cms-page__back">
          &larr; Back to Sites
        </Link>
        <div className="cms-page__header-right">
          {site && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setTailorOpen(true)}
              aria-label="Tailor site for a target role using AI"
            >
              Tailor for target role
            </Button>
          )}
          {site && (
            <PublishButton
              isDirty={site.is_dirty}
              isPublishing={isPublishing}
              lastPublishedAt={site.last_published_at}
              onPublish={() => {
                if (id) void publishSite(id);
              }}
            />
          )}
        </div>
      </header>

      {updateError && (
        <div className="editor-page__save-msg editor-page__save-msg--error">
          {updateError}
        </div>
      )}

      {/* Only surface errors that prevent site loading; sub-request errors
          (sections, templates) must not hide an already-rendered site page. */}
      <LoadingState loading={isLoading && !site} error={!site ? error : null}>
        {!site && !isLoading ? (
          <div className="cms-page__not-found">
            <p>Site not found.</p>
            <Button variant="secondary" size="sm" onClick={() => navigate('/cms/sites')}>
              Go to Sites
            </Button>
          </div>
        ) : site ? (
          <div className="cms-page__body">
            <h1 className="cms-page__site-name">{site.name}</h1>
            <p className="cms-page__site-domain">{site.domain}</p>

            {/* ---------- Template Selector ---------- */}
            <Panel>
              <Panel.Header subtitle={templates.find((t) => t.id === site.template_id)?.name}>Template</Panel.Header>
              <Panel.Content>
                {templates.length === 0 ? (
                  <p className="list-empty">No templates available.</p>
                ) : (
                  <div className="cms-template-grid">
                    {templates.map((tpl) => (
                      <button
                        key={tpl.id}
                        type="button"
                        className={`cms-template-card ${site.template_id === tpl.id ? 'cms-template-card--selected' : ''}`}
                        onClick={() => handleSelectTemplate(tpl.id)}
                      >
                        <div className="cms-template-card__thumb">
                          {tpl.thumbnail_url ? (
                            <img
                              src={tpl.thumbnail_url}
                              alt={tpl.name}
                              className="cms-template-card__img"
                            />
                          ) : (
                            <span className="cms-template-card__placeholder">
                              {tpl.name.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <span className="cms-template-card__name">{tpl.name}</span>
                        <div className="cms-template-card__badges">
                          {tpl.supports_pdf && (
                            <span className="badge badge--active">PDF</span>
                          )}
                          {tpl.supports_word && (
                            <span className="badge badge--active">Word</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {site.template_id && (
                  <div>
                    <AiAssistButton
                      onClick={() =>
                        void personalizeAi.trigger({
                          profile_summary: personalInfo?.summary ?? site.name,
                          target_role: personalInfo?.tagline ?? '',
                          template_id: site.template_id,
                        })
                      }
                      isLoading={personalizeAi.isLoading}
                      disabled={!site.template_id}
                      label="Personalize template with AI"
                      size="md"
                    />
                    <AiSuggestionPanel
                      suggestion={personalizeAi.suggestion as Record<string, unknown> | null}
                      error={personalizeAi.error}
                      isLoading={personalizeAi.isLoading}
                      onAccept={personalizeAi.accept}
                      onDismiss={personalizeAi.dismiss}
                      renderSuggestion={renderRecommendations}
                    />
                  </div>
                )}
              </Panel.Content>
            </Panel>

            {/* ---------- Section Manager ---------- */}
            <Panel>
              <Panel.Header>Sections</Panel.Header>
              <Panel.Content>
                <SortableList
                  items={sections}
                  onReorder={handleSectionReorder}
                  emptyMessage="No sections configured."
                  renderItem={(section) => (
                    <div className="cms-section-item">
                      <VisibilityToggle
                        label={SECTION_LABELS[section.section_name] ?? section.section_name}
                        checked={section.is_visible}
                        onChange={(checked) => handleSectionVisibility(section.id, checked)}
                      />
                    </div>
                  )}
                />
              </Panel.Content>
            </Panel>

            {/* ---------- Content Tabs ---------- */}
            <div className="cms-tabs">
              {(['career', 'skills', 'education', 'certificates', 'patents'] as ContentTab[]).map(
                (tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`cms-tabs__tab ${activeTab === tab ? 'cms-tabs__tab--active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ),
              )}
            </div>

            {/* ---------- Career Tab ---------- */}
            {activeTab === 'career' && (
              <Panel defaultCollapsed={false}>
                <Panel.Header>Career Entries</Panel.Header>
                <Panel.Content>
                  <SortableList
                    items={siteCareerEntries}
                    onReorder={handleCareerReorder}
                    emptyMessage="No career entries assigned to this site."
                    renderItem={(entry) => {
                      const entryVariants = getVariantsForEntry(entry.career_entry_id);
                      return (
                        <div className="cms-content-item">
                          <VisibilityToggle
                            label={getCareerEntryName(entry.career_entry_id)}
                            checked={entry.is_visible}
                            onChange={(checked) =>
                              handleCareerVisibility(entry.id, checked)
                            }
                          />
                          <select
                            className="input cms-variant-select"
                            value={entry.variant_id ?? ''}
                            onChange={(e) =>
                              handleVariantSelect(
                                entry.career_entry_id,
                                e.target.value || null,
                              )
                            }
                            aria-label="Select variant"
                          >
                            <option value="">Base</option>
                            {entryVariants.map(
                              (v) => (
                                <option key={v.id} value={v.id}>
                                  {v.label}
                                </option>
                              ),
                            )}
                          </select>
                          {entryVariants.length > 0 && (
                            <button
                              type="button"
                              className="variant-picker__badge"
                              onClick={() => openVariantPicker(entry)}
                              aria-label={`Manage ${entryVariants.length} variant${entryVariants.length === 1 ? '' : 's'} for ${getCareerEntryName(entry.career_entry_id)}`}
                            >
                              Variants ({entryVariants.length})
                            </button>
                          )}
                        </div>
                      );
                    }}
                  />
                </Panel.Content>
              </Panel>
            )}

            {/* ---------- Skills Tab ---------- */}
            {activeTab === 'skills' && (
              <Panel defaultCollapsed={false}>
                <Panel.Header>Skills</Panel.Header>
                <Panel.Content>
                  <SortableList
                    items={siteSkills}
                    onReorder={handleSkillReorder}
                    emptyMessage="No skills assigned to this site."
                    renderItem={(skill) => (
                      <div className="cms-content-item">
                        <VisibilityToggle
                          label={getSkillName(skill.skill_id)}
                          checked={skill.is_visible}
                          onChange={(checked) =>
                            handleSkillVisibility(skill.id, checked)
                          }
                        />
                      </div>
                    )}
                  />
                </Panel.Content>
              </Panel>
            )}

            {/* ---------- Education Tab ---------- */}
            {activeTab === 'education' && (
              <Panel defaultCollapsed={false}>
                <Panel.Header>Education</Panel.Header>
                <Panel.Content>
                  <SortableList
                    items={siteEducation}
                    onReorder={handleEducationReorder}
                    emptyMessage="No education entries assigned to this site."
                    renderItem={(edu) => (
                      <div className="cms-content-item">
                        <VisibilityToggle
                          label={getEducationName(edu.education_id)}
                          checked={edu.is_visible}
                          onChange={(checked) =>
                            handleEducationVisibility(edu.id, checked)
                          }
                        />
                      </div>
                    )}
                  />
                </Panel.Content>
              </Panel>
            )}

            {/* ---------- Certificates Tab ---------- */}
            {activeTab === 'certificates' && (
              <Panel defaultCollapsed={false}>
                <Panel.Header>Certificates</Panel.Header>
                <Panel.Content>
                  <SortableList
                    items={siteCertificates}
                    onReorder={handleCertificateReorder}
                    emptyMessage="No certificates assigned to this site."
                    renderItem={(cert) => (
                      <div className="cms-content-item">
                        <VisibilityToggle
                          label={getCertificateName(cert.certificate_id)}
                          checked={cert.is_visible}
                          onChange={(checked) =>
                            handleCertificateVisibility(cert.id, checked)
                          }
                        />
                      </div>
                    )}
                  />
                </Panel.Content>
              </Panel>
            )}

            {/* ---------- Patents Tab ---------- */}
            {activeTab === 'patents' && (
              <Panel defaultCollapsed={false}>
                <Panel.Header>Patents</Panel.Header>
                <Panel.Content>
                  <SortableList
                    items={sitePatents}
                    onReorder={handlePatentReorder}
                    emptyMessage="No patents assigned to this site."
                    renderItem={(pat) => (
                      <div className="cms-content-item">
                        <VisibilityToggle
                          label={getPatentTitle(pat.patent_id)}
                          checked={pat.is_visible}
                          onChange={(checked) =>
                            handlePatentVisibility(pat.id, checked)
                          }
                        />
                      </div>
                    )}
                  />
                </Panel.Content>
              </Panel>
            )}
          </div>
        ) : null}
      </LoadingState>
      {site && id && (
        <TailorPanel
          siteId={id}
          open={tailorOpen}
          onClose={() => setTailorOpen(false)}
          onApplied={(msg) => {
            setUpdateError(null);
            log.info('Tailor applied', { msg });
            void loadSiteContent(id);
          }}
        />
      )}
      {site && id && variantPickerEntry && (
        <VariantPickerDrawer
          siteId={id}
          entryId={variantPickerEntry.entryId}
          entryLabel={variantPickerEntry.entryLabel}
          currentVariantId={variantPickerEntry.currentVariantId}
          open={variantPickerOpen}
          onClose={() => setVariantPickerOpen(false)}
          onVariantChanged={() => {
            void loadSiteContent(id);
            for (const entry of careerEntries) {
              void fetchVariants(entry.id);
            }
          }}
        />
      )}
    </div>
  );
}
